<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ t('pages.campaigns.candidateList.modal.submitAnswer.title') }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <Multiselect
            v-model="rawCandidate.declineReason"
            :options="declineReasonOptions"
            :label="`${$t('pages.campaigns.candidateList.modal.submitAnswer.declineReason')}`"
            :close-on-select="true"
            :filter-results="true"
            :error="errors?.declineReason"
            :can-clear="false"
            :can-deselect="false"
            class="w-full"
            required
          />
          <div class="w-full text-center">
            <custom-button variant="primary" class="mt-15" :busy="loading" @click="save">
              {{ $t('save') }}
            </custom-button>
          </div>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import Multiselect from '@utils/Multiselect.vue';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import CustomButton from '@utils/CustomButton.vue';
import { syncRef } from '@hooks/state';
import { enumOptionsProvider } from '@utils/table/hooks/provider';
import type { CampaignCandidate } from '@/entities/Entities';

interface Props {
  open: boolean;
  candidate: CampaignCandidate | undefined;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);
const internalCandidate = useVModel(props, 'candidate', emit);

const { t } = useI18n();
const notify = useNotify();

const rawCandidate = syncRef(internalCandidate, (c) => ({ ...c }));

const {
  data: result,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    comment: string(),
  }),
  rawCandidate,
);

function resetData() {
  rawCandidate.value.comment = '';
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const declineReasonOptions = enumOptionsProvider('/enums/CandidateDeclineReason', (item) => ({
  value: item,
  label: t(`candidate.enum.declineReason.${item}`),
}));

const save = validatedFn(async () => {
  try {
    // answer for the driver
    await exec('/campaign_candidates/answer_submission', {
      method: 'POST',
      body: {
        candidateDeclineReason: rawCandidate.value.declineReason,
        driver: rawCandidate.value.driver?.['@id'],
        campaign: `/campaigns/${rawCandidate.value.campaign?.id}`,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.campaigns.candidateList.modal.new.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.campaigns.candidateList.modal.new.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
